import { useEffect, useRef } from "react";
import type { Customer, DispatchStop, Resource } from "@/lib/types.js";

/**
 * Real map view per ServiceTitan/Onfleet pattern (PRD §"Dispatch map view"). Uses
 * Leaflet + OpenStreetMap tiles — no API key required, runtime-loaded so the bundle
 * stays small and so the map degrades gracefully when offline / on slow networks.
 *
 * Renders:
 *   • Tiles from openstreetmap.org
 *   • Marker for the resource's home_base (red)
 *   • Markers for each stop's customer (numbered by visit order)
 *   • Polyline through home_base → stop_1 → stop_2 → … → stop_n
 *
 * Falls back to a lightweight SVG polyline when Leaflet fails to load (offline,
 * test environment, etc.) — same shape as the previous implementation.
 */
export function DispatchMap({
  resource,
  stops,
  customers
}: {
  resource: Resource;
  stops: DispatchStop[];
  customers: Map<string, Customer>;
}): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const points = collectPoints(resource, stops, customers);

  useEffect(() => {
    let cancelled = false;
    let mapInstance: { remove: () => void } | null = null;

    if (points.length < 2 || !containerRef.current) return;

    void (async () => {
      try {
        const L = await loadLeaflet();
        if (cancelled || !containerRef.current) return;

        // Initialise once per mount; React StrictMode may double-invoke effects in dev.
        const node = containerRef.current as HTMLDivElement & { _leaflet_id?: number };
        if (node._leaflet_id !== undefined) return;

        const map = L.map(node, { attributionControl: true, zoomControl: true });
        mapInstance = map;

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution: "© OpenStreetMap"
        }).addTo(map);

        const latlngs = points.map((p) => [p.lat, p.lng] as [number, number]);
        L.polyline(latlngs, { color: "#3b82f6", weight: 3, opacity: 0.8 }).addTo(map);

        // Numbered stop markers using a divIcon so the route order is visible
        // on the map itself, not only on hover. The home base gets a red H
        // marker; subsequent stops get a blue 1, 2, 3… in visit order.
        let stopOrdinal = 0;
        points.forEach((p, i) => {
          const isHome = i === 0 && p.kind === "home";
          const label = isHome ? "H" : String(++stopOrdinal);
          const bg = isHome ? "#dc2626" : "#3b82f6";
          const icon = L.divIcon({
            className: "calendar-stop-marker",
            iconSize: [22, 22],
            iconAnchor: [11, 11],
            html: `
              <div style="
                width: 22px;
                height: 22px;
                border-radius: 9999px;
                background: ${bg};
                color: white;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 11px;
                font-weight: 600;
                font-family: ui-sans-serif, system-ui, sans-serif;
                box-shadow: 0 1px 2px rgba(0,0,0,0.4);
                border: 2px solid white;
              ">${label}</div>
            `
          });
          const marker = L.marker([p.lat, p.lng], { icon }).addTo(map);
          marker.bindTooltip(p.label, { direction: "top" });
        });

        map.fitBounds(latlngs, { padding: [16, 16], maxZoom: 14 });
      } catch {
        // Leaflet failed to load — the SVG fallback still renders below.
      }
    })();

    return () => {
      cancelled = true;
      mapInstance?.remove();
    };
  }, [points]);

  if (points.length < 2) {
    return (
      <div className="rounded border bg-muted/40 p-3 text-center text-[10px] text-muted-foreground">
        No geocoded stops
      </div>
    );
  }

  return (
    <div className="relative h-32 w-full overflow-hidden rounded border bg-muted/30">
      <div ref={containerRef} className="absolute inset-0" data-testid="dispatch-leaflet" />
      {/* SVG fallback rendered behind the map. Visible until Leaflet finishes loading
          (or always, if loading fails). */}
      <SvgFallback points={points} />
    </div>
  );
}

interface MapPoint {
  lat: number;
  lng: number;
  kind: "home" | "stop";
  label: string;
}

function collectPoints(
  resource: Resource,
  stops: DispatchStop[],
  customers: Map<string, Customer>
): MapPoint[] {
  const points: MapPoint[] = [];
  if (resource.home_base) {
    points.push({
      lat: resource.home_base.lat,
      lng: resource.home_base.lng,
      kind: "home",
      label: `${resource.name} (home)`
    });
  }
  stops.forEach((stop, i) => {
    const customer = customers.get(stop.appointment.customer_id);
    if (customer?.lat !== undefined && customer.lng !== undefined) {
      points.push({
        lat: customer.lat,
        lng: customer.lng,
        kind: "stop",
        label: `Stop ${i + 1}: ${customer.name ?? ""}`
      });
    }
  });
  return points;
}

interface LeafletMarker {
  addTo: (m: unknown) => LeafletMarker;
  bindTooltip: (txt: string, options?: object) => LeafletMarker;
}

interface LeafletMap {
  addTo: (m: unknown) => unknown;
  fitBounds: (bounds: Array<[number, number]>, options?: object) => unknown;
  remove: () => void;
}

interface LeafletDivIcon {
  /** opaque — passed back to L.marker(..., { icon }). */
  __divIcon: true;
}

interface LeafletNamespace {
  map: (el: HTMLElement, options?: object) => LeafletMap;
  tileLayer: (url: string, options?: object) => { addTo: (m: unknown) => unknown };
  polyline: (
    latlngs: Array<[number, number]>,
    options?: object
  ) => { addTo: (m: unknown) => unknown };
  circleMarker: (latlng: [number, number], options?: object) => LeafletMarker;
  marker: (latlng: [number, number], options?: { icon?: LeafletDivIcon }) => LeafletMarker;
  divIcon: (options: {
    html: string;
    className?: string;
    iconSize?: [number, number];
    iconAnchor?: [number, number];
  }) => LeafletDivIcon;
}

let leafletPromise: Promise<LeafletNamespace> | null = null;

async function loadLeaflet(): Promise<LeafletNamespace> {
  if (leafletPromise) return leafletPromise;
  leafletPromise = (async () => {
    // Inject CSS once.
    const styleId = "leaflet-css";
    if (typeof document !== "undefined" && !document.getElementById(styleId)) {
      const link = document.createElement("link");
      link.id = styleId;
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      link.integrity = "sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=";
      link.crossOrigin = "";
      document.head.appendChild(link);
    }
    // Dynamic ESM import via CDN keeps Leaflet out of the main bundle.
    const cdnUrl = "https://unpkg.com/leaflet@1.9.4/dist/leaflet-src.esm.js";
    const mod = (await import(/* @vite-ignore */ cdnUrl)) as {
      default?: LeafletNamespace;
    } & LeafletNamespace;
    const L = (mod.default ?? mod) as LeafletNamespace;
    return L;
  })();
  return leafletPromise;
}

/** Lightweight SVG polyline kept as a graceful fallback when Leaflet is unavailable. */
function SvgFallback({ points }: { points: MapPoint[] }): React.JSX.Element {
  const lats = points.map((p) => p.lat);
  const lngs = points.map((p) => p.lng);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const w = Math.max(maxLng - minLng, 0.001);
  const h = Math.max(maxLat - minLat, 0.001);
  const project = (lat: number, lng: number): { x: number; y: number } => ({
    x: ((lng - minLng) / w) * 100,
    y: 100 - ((lat - minLat) / h) * 100
  });
  const projected = points.map((p) => project(p.lat, p.lng));
  const path = projected.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ");
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className="absolute inset-0 h-full w-full opacity-60"
      aria-hidden
    >
      <path d={path} fill="none" stroke="hsl(var(--primary))" strokeWidth="1.4" />
      {projected.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r={i === 0 ? 2 : 1.4}
          fill={i === 0 ? "hsl(var(--destructive))" : "hsl(var(--primary))"}
        />
      ))}
    </svg>
  );
}
